import React, { useContext, useEffect, useMemo, useRef } from "react";
import { NavLink } from "react-router-dom";
import { getLang } from "../../features/app/appSlice";
import { useSelector } from "react-redux";

export default function SegmentPageTemplate({
  children,
  title,
  description,
  img,
  link = "",
}: {
  children: any;
  title: any;
  description: string;
  img: any;
  link?: string;
}) {
  const url = window.location.pathname.split("/").pop() || ""; // get the "section" from the URL
  const lang = useSelector(getLang);
  const segments = useMemo(
    () => [
      {
        url: "family-office",
        title: { en: "Family Office", cn: "家族办公室" },
      },
      { url: "real-estate", title: { en: "Real Estate", cn: "房地产" } },
      {
        url: "technology-investment",
        title: { en: "Technology Investment", cn: "科技投资" },
      },
      {
        url: "art-culture",
        title: { en: "Art & Culture", cn: "艺术与文化" },
      },
      { url: "private-club", title: { en: "Private Club", cn: "私人俱乐部" } },
      { url: "think-tank", title: { en: "Think Tank", cn: "智库" } },
      {
        url: "cambridge-foundation",
        title: { en: "Cambridge Foundation", cn: "剑桥基金会" },
      },
    ],
    []
  ); // Empty dependency array means this will only run once

  const navLinkRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  useEffect(() => {
    // Find the index of the selected segment
    const selectedIndex = segments.findIndex(
      (segment: any) => segment.url === url
    );
    if (selectedIndex !== -1 && navLinkRefs.current[selectedIndex]) {
      // Scroll the selected NavLink into view
      navLinkRefs.current[selectedIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [url, segments]);

  return (
    <div className="mt-20 md:mt-32">
      <div className="content-container pt-2 sm:pt-6">
        <p className="tag">{lang ? "WHAT WE DO" : "为您服务"}</p>
        <p className="text-4xl sm:text-5xl font-poster pt-3 pb-6 text-secondary-dark leading-tight">
          {lang ? "Our Businesses" : "我们的业务"}
        </p>
        <div className="border-b border-secondary-dark/30 mb-8 flex gap-8 whitespace-nowrap overflow-x-scroll hide-scrollbar">
          {segments.map((i: any, index: any) => {
            const active = i.url === url;
            return (
              <NavLink
                key={i.url}
                to={"/business/" + i.url}
                className={`pb-3 text-[13.5px] tracking-wide transition-colors ${
                  active
                    ? "font-medium text-secondary-dark border-b border-secondary-dark"
                    : "text-secondary-dark/60 hover:text-secondary-dark"
                }`}
                ref={(el) => (navLinkRefs.current[index] = el)}
              >
                {lang ? i.title.en : i.title.cn}
              </NavLink>
            );
          })}
        </div>
        <div className="relative">
          <div className="relative overflow-hidden">
            <img
              className="h-[420px] sm:h-[480px] object-cover object-center w-full"
              src={img.src}
              alt={
                title +
                (lang
                  ? "'s background decorative image. "
                  : "的背景装饰图像。") +
                img.alt
              }
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/80"></div>
          </div>
          <div className="absolute bottom-8 sm:bottom-10 left-6 sm:left-10 right-6 sm:right-10">
            <div className="flex items-center gap-3 mb-5">
              <span className="bg-white text-secondary-dark text-[10px] tracking-[0.3em] uppercase px-3 py-1 font-medium">
                {lang ? "Our Businesses" : "我们的业务"}
              </span>
              {link && (
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-white text-[11px] tracking-[0.3em] uppercase border border-white/40 px-3 py-1 hover:bg-white hover:text-secondary-dark transition-colors"
                >
                  {lang ? "Enter Website" : "进入网站"}
                  <span className="text-base leading-none">↗</span>
                </a>
              )}
            </div>
            <h1 className="text-white text-5xl sm:text-6xl font-poster leading-[1.05]">
              {title}
            </h1>
          </div>
        </div>
        <p className="mt-10 mb-24 text-secondary-dark/85 leading-8 text-[15px] max-w-[920px]">
          {description}
        </p>
      </div>
      {children}
    </div>
  );
}
